import { Dialog, notifyError, notifySuccess } from '@/components'
import { useAddNewLightningUnitMutation } from '@/modules/add-new-unit/services/newUnitService'
import { AddNewLightningUnitReq } from '@/modules/add-new-unit/types/newUnitType'
import FormAddHeader from '@/components/Molecules/FormAddHeader/FormAddHeader'
import { UnitSearchReq } from '@/modules/devices-list/types/newUnitsType'
import { useGetUnitsHomeMapMutation } from '@/modules/maps/services/homeMapService'
import { timeout } from '@/utils'
import MapLightnings from '@/components/Organisms/Map/MapLightnings'
import { useChangeSet } from '@/utils/useChangeSet'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import SystemUploadImg from '@/components/Organisms/SystemUploadImg/SystemUploadImg'
import { Box, InputAdornment } from '@mui/material'
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import Input from '@/components/Molecules/Input/Input'
import CustomSelect from '@/components/CustomSelect/CustomSelect'
import UploadFeaturesDoc from '@/components/Organisms/UploadFeaturesDoc/UploadFeaturesDoc'
import UploadFeaturesDocList from '@/components/Organisms/UploadFeaturesDoc/UploadFeaturesDocList'
import type { PendingUploadedDocument } from '@/components/Organisms/UploadFeaturesDoc/service'
import { sxWrapper } from '@/pages/Components/AddPagesWrapper'
interface IUnitData {
  setLoader: Dispatch<SetStateAction<boolean>>
}

const center = [47.2313, 39.7233]

export interface IUnitsFields {
  name: string
  address: string
  lightingLength: number | ''
  lightingPoles: number | ''
  lightingLuminaires: number | ''
  lightingPower: number | ''
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Имя не может быть пустым')
    .trim('Имя не может быть пустым')
    .max(30, 'Поле должно содержать не более 30 символов'),
  address: Yup.string()
    .required('Адрес не может быть пустым')
    .trim('Адрес не может быть пустым')
    .max(250, 'Поле должно содержать не более 250 символов'),
  lightingLength: Yup.number()
    .optional()
    .nullable()
    .transform((val) => (val === '' || val === null ? undefined : val))
    .typeError('Введите число'),
  lightingPoles: Yup.number()
    .optional()
    .nullable()
    .transform((val) => (val === '' || val === null ? undefined : val))
    .typeError('Введите число'),
  lightingLuminaires: Yup.number()
    .optional()
    .nullable()
    .transform((val) => (val === '' || val === null ? undefined : val))
    .typeError('Введите число'),
  lightingPower: Yup.number()
    .optional()
    .nullable()
    .transform((val) => (val === '' || val === null ? undefined : val))
    .typeError('Введите число'),
})

export const LIGHTNING_STATUS_SELECT = [
  {
    id: 1,
    name: 'Существующее',
    typeId: 1,
  },
  {
    id: 2,
    name: 'Перспективное',
    typeId: 2,
  },
  {
    id: 3,
    name: 'Прочие',
    typeId: 3,
  },
]

const FormLamp: FC<IUnitData> = ({ setLoader }) => {
  const navigate = useNavigate()

  const [addLightningUnitService] = useAddNewLightningUnitMutation()
  const [getDataUnits, { data: locations }] = useGetUnitsHomeMapMutation()
  const getLightsLocations: number[][] = []

  const [points, setPoints] = useState<number[][]>([])
  const [lightStatus, setLightStatus] = useState<1 | 2>(1)

  const onGetUnits = async () => {
    try {
      const usersSearchData: Omit<UnitSearchReq, 'page'> = {
        value: '',
        deviceTypeFilter: [0],
        deviceStateFilter: [0],
        favoriteId: 0,
        lightingTypeId: 0,
      }
      await getDataUnits(usersSearchData).unwrap()
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    onGetUnits().then()
  }, [])

  const [openDialogSave, setOpenDialogSave] = useState(false)
  const [imageId, setImageId] = useState<string | null>(null)
  const [favoriteCategoryIds, setFavoriteCategoryIds] = useState<number[]>([])

  const defaultValues: IUnitsFields = {
    name: '',
    address: '',
    lightingLength: '',
    lightingPoles: '',
    lightingLuminaires: '',
    lightingPower: '',
  }

  const { handleSubmit, control, reset, trigger, formState, watch, getValues } =
    useForm<IUnitsFields>({
      defaultValues,
      resolver: yupResolver(validationSchema),
      criteriaMode: 'all',
      reValidateMode: 'onChange',
    })

  const [attachedDocuments, setAttachedDocuments] = useState<
    PendingUploadedDocument[]
  >([])

  useEffect(() => {
    reset({
      name: '',
      address: '',
      lightingLength: '',
      lightingPoles: '',
      lightingLuminaires: '',
      lightingPower: '',
    })
  }, [])

  const onSaveBtnClick = () => {
    trigger().then(() => {
      if (!getValues('name') || !getValues('address')) {
        return
      }
      if (!formState.errors.name && !formState.errors.address) {
        setOpenDialogSave(true)
      }
    })
  }

  const onSubmit = async (data: IUnitsFields) => {
    try {
      setOpenDialogSave(false)
      setLoader(true)
      const newUnit: AddNewLightningUnitReq = {
        id: 0,
        name: data.name,
        address: data.address,
        typeId: lightStatus,
        favoriteCategoryIds,
        lightingTypeId: lightStatus,
        coordinates: JSON.stringify(points),
        imageId: imageId ?? '',
        documentIds: attachedDocuments.map((d) => d.id),
        lightingLength:
          data.lightingLength === '' ? undefined : Number(data.lightingLength),
        lightingPoles:
          data.lightingPoles === '' ? undefined : Number(data.lightingPoles),
        lightingLuminaires:
          data.lightingLuminaires === ''
            ? undefined
            : Number(data.lightingLuminaires),
        lightingPower:
          data.lightingPower === '' ? undefined : Number(data.lightingPower),
      }
      const res = await addLightningUnitService(newUnit).unwrap()
      if (res.success) {
        notifySuccess('Новое устройство успешно добавлено!', 1000)
        await timeout(1000)
        navigate('/new-units')
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoader(false)
    }
  }

  useChangeSet(() => {
    if (watch('name').length) {
      trigger('name')
    }
    if (watch('address').length) {
      trigger('address')
    }
  }, [watch('name'), watch('address')])

  return (
    <>
      <Dialog
        message='Изменения будут сохранены, продолжить?'
        open={openDialogSave}
        handleClose={() => setOpenDialogSave(false)}
        handleAgree={() => {
          if (Object.keys(formState.errors).length >= 1) {
            notifyError('Исправте ошибки в форме')
          }
          handleSubmit(onSubmit)()
        }}
      />
      <Box sx={sxWrapper}>
        <FormAddHeader
          title={'Добавление освещения'}
          onFavoriteListChange={setFavoriteCategoryIds}
        />
        <Box className={'wrapper'}>
          <Box className={'left'}>
            <Box className={'row'}>
              <CustomSelect
                items={LIGHTNING_STATUS_SELECT}
                defaultValue={lightStatus}
                label='Выберите статус освещения'
                onChange={(e) => {
                  const value = (e?.target as HTMLInputElement)?.value
                  setLightStatus(value ? (Number(value) as 1 | 2) : 1)
                }}
              />
              <Input
                name='lightingLength'
                type='number'
                label='Протяженность'
                control={control}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>м</InputAdornment>
                  ),
                }}
              />
              <Input
                name='lightingPoles'
                type='number'
                label='Опоры'
                control={control}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>шт</InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box className={'row'}>
              <Input
                name='name'
                type='text'
                label='Введите название'
                control={control}
              />

              <Input
                name='lightingLuminaires'
                type='number'
                label='Светильники'
                control={control}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>шт</InputAdornment>
                  ),
                }}
              />
              <Input
                name='lightingPower'
                type='number'
                label='Мощность'
                control={control}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>кВт</InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box className={'row'}>
              <Input
                name='address'
                type='text'
                label='Адрес установки'
                control={control}
              />
            </Box>
          </Box>
          <Box className={'right'}>
            {attachedDocuments.length > 0 ? (
              <UploadFeaturesDocList
                documents={attachedDocuments}
                deviceId={0}
                onDeleted={(docId) =>
                  setAttachedDocuments((prev) =>
                    prev.filter((d) => d.id !== docId)
                  )
                }
              />
            ) : (
              <Box>Прикрепленные файлы: 0</Box>
            )}
          </Box>
        </Box>

        <Box className={'mapContainer'}>
          <MapLightnings
            typeId={lightStatus}
            defaultCenter={center}
            defaultZoom={12}
            dataUnits={locations}
            staticPolylines={getLightsLocations || []}
            initialCoordinates={points}
            onCoordinatesChange={(coords) => {
              setPoints(coords)
            }}
          />
        </Box>
        <Box className={'footerButtons'}>
          <SystemUploadImg
            imageId={imageId}
            onImageUploaded={setImageId}
          />
          <UploadFeaturesDoc
            showDownloadAll={false}
            onDocumentsUploaded={(items) =>
              setAttachedDocuments((prev) => [...prev, ...items])
            }
          />
          <LoadingButton
            loading={false}
            variant='outlined'
            onClick={onSaveBtnClick}
            disabled={
              !formState.isDirty && !imageId && attachedDocuments.length === 0
            }
          >
            <span>Сохранить</span>
          </LoadingButton>
        </Box>
      </Box>
    </>
  )
}

export default FormLamp
